import { useEffect, useRef } from 'react'
import { NavLink, Outlet, matchPath, useLocation } from 'react-router'
import { emit } from '../../platform/analytics/analytics'
import type { ENUM_VALUES } from '../../platform/analytics/vocabulary'
import { Icon } from '../../platform/icons/Icon'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { SETTINGS_NAV } from '../../shell/nav'
import './settingsLayout.css'

/**
 * The settings sub-navigation column — #205.
 *
 * The design does not put Profile, Preferences, Security, Devices and Plan &
 * billing in the sidebar. The sidebar's foot carries ONE Settings link, and
 * opening it renders this column beside the page content.
 *
 * ## Why this is a second `<nav>` where the sidebar's foot is not
 *
 * `Sidebar` keeps its foot inside the main `<nav>` deliberately — a second
 * landmark there would describe one sidebar as two things. This column is
 * different: it is a genuinely separate navigation region, scoped to a
 * subtree, and it appears and disappears with the route. A screen-reader user
 * listing landmarks should find it, which is why it has both the element and
 * an accessible name.
 *
 * ## The heading
 *
 * `SETTINGS` is a heading for the region, not a decorative label, so it is a
 * real element referenced by `aria-labelledby` rather than a styled `<span>`
 * with the name duplicated into an `aria-label`. Two copies of a name is how
 * one of them goes stale.
 *
 * ## Lime
 *
 * The active item takes `--accent`, matching the design and the sidebar's own
 * active marker. Permitted under CLAUDE.md's rule as settled in #197: active
 * sub-navigation is persistent chrome and sits outside the one-fill-per-
 * content-area count. It carries the same non-colour signals the sidebar's
 * marker does — weight and `aria-current` — because the fill alone measures
 * 1.07:1 against its backdrop in light theme.
 */
type Section = (typeof ENUM_VALUES.section)[number]

/** The analytics enum for each destination; the key is the `SETTINGS_NAV` path. */
const SECTION_OF_PATH: Record<string, Section> = {
  '/settings/profile': 'profile',
  '/settings/preferences': 'preferences',
  '/settings/security': 'security',
  '/settings/devices': 'devices',
  '/settings/billing': 'billing',
  // Spread from the same inline MODE check `SETTINGS_NAV` uses: a bare entry would
  // put the path in a production bundle, which `backendOverride.build.test.ts` forbids.
  ...(import.meta.env.MODE !== 'production'
    ? ({ '/settings/developer': 'developer' } as const)
    : {}),
}

/** The section a pathname belongs to, or `null` when it matches no destination. */
function sectionFor(pathname: string): Section | null {
  const item = SETTINGS_NAV.find((i) => matchPath({ path: i.path, end: false }, pathname))
  return item ? (SECTION_OF_PATH[item.path] ?? null) : null
}

export function SettingsLayout() {
  const hitTarget = useHitTarget('page')
  const breakpoint = useBreakpoint()
  const { pathname } = useLocation()
  const activeRef = useRef<HTMLAnchorElement>(null)
  const section = sectionFor(pathname)

  /**
   * `settings_section_viewed`: once per section visit. The ref holds the last
   * section emitted, so a re-render, a repeat click on the current tab and
   * StrictMode's second effect run (refs survive it) all emit nothing; moving
   * to another section and back is two views.
   */
  const lastSection = useRef<Section | null>(null)
  useEffect(() => {
    if (section === null || section === lastSection.current) return
    lastSection.current = section
    emit('settings_section_viewed', { section })
  }, [section])

  /**
   * Below desktop the column becomes a horizontal strip — #211.
   *
   * `.settings__nav` was `width: 196px; flex: none`, and `flex: none` is not
   * shrinkable: `.settings__content { min-width: 0 }` lets the CONTENT collapse
   * to nothing and cannot touch its sibling. At 320px the shell offered 207px
   * and the column alone claimed 196 + 28, so `/settings/security` scrolled the
   * document sideways by 94px. Even with the sidebar gone (#109) the column
   * would leave 52px for the page.
   *
   * **Reshaped, not removed.** #210 originally said the column should "stack or
   * collapse", and #211 rejected that: it demotes five destinations behind a
   * disclosure on the viewport where they are hardest to find. The app already
   * does the right thing elsewhere — the sidebar becomes a rail rather than
   * disappearing, and now a tab bar rather than a rail.
   *
   * `useBreakpoint()`, not a media query. `settingsLayout.css` may not declare
   * one and `single-source.test.ts` rejects it.
   */
  const strip = breakpoint !== 'desktop' && breakpoint !== 'wide'

  /**
   * Bring the current destination into view — #211's fourth criterion.
   *
   * Landing on `/settings/billing` with the strip scrolled to the start shows
   * the user no indication of where they are, and the strip is exactly wide
   * enough for that to happen at 320px.
   *
   * **Only when there IS a strip.** The column does not scroll, so calling this
   * at desktop would find the nearest scrollable ancestor — the page — and
   * hijack its scroll position on every settings navigation.
   *
   * `block: 'nearest'` for the same reason in the other axis: without it the
   * browser also scrolls vertically to centre the link, which moves the page
   * under a user who only changed tab. Guarded because jsdom implements no
   * `scrollIntoView` at all, and a settings screen must not throw in a test
   * environment that lacks it.
   */
  useEffect(() => {
    if (!strip) return
    activeRef.current?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' })
  }, [strip, pathname])

  return (
    <div className={`settings${strip ? ' settings--stacked' : ''}`}>
      <nav
        className={`settings__nav${strip ? ' settings__nav--strip' : ''}`}
        aria-labelledby="settings-nav-heading"
      >
        {/* The heading names the region in both shapes. In the strip it is
            visually hidden rather than dropped: `aria-labelledby` points at it,
            so removing it would leave the second navigation landmark unnamed —
            and a 196px-wide "SETTINGS" laid out in a horizontal row would eat
            the space the destinations need.
            `sr-only` from reset.css, composed rather than restated — this file
            had a fourth hand-rolled copy of the clip shape until web-architect
            counted them. */}
        <h2
          id="settings-nav-heading"
          className={`settings__nav-heading${strip ? ' sr-only' : ''}`}
        >
          Settings
        </h2>
        {SETTINGS_NAV.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className="settings__link hoverable"
            style={{ minHeight: hitTarget }}
            // Keyed off the route we already have, not off the `aria-current`
            // react-router writes. The first version read that attribute inside
            // a callback ref — it worked, but it depended on another library's
            // attribute timing and left a stale ref on any route not in
            // SETTINGS_NAV. `pathname` is two lines up.
            // `matchPath`, not `startsWith`: the latter matches two entries the
            // day one settings path is a prefix of another, and the last one
            // silently takes the ref. `ChromeLayout` already answers this exact
            // question this way.
            ref={matchPath({ path: item.path, end: false }, pathname) ? activeRef : null}
          >
            <Icon glyph={item.icon} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* No ErrorBoundary here: `ChromeLayout` already wraps the Outlet, so a
          throw in a settings screen is caught there with the shell intact.
          A second boundary would only change which chrome survives, and this
          column is not worth keeping when the page it navigates has failed. */}
      <div className="settings__content">
        <Outlet />
      </div>
    </div>
  )
}
